'use client';

import React from 'react';
import { MdSearch } from 'react-icons/md';

import type * as stats from '@blockscout/stats-types';
import type { StatsIntervalIds } from 'types/client/stats';

import ChartIntervalSelect from 'src/components/chart/ChartIntervalSelect';

import styles from './StatsFilters.module.css';

type Props = {
  sections?: Array<stats.LineChartSection>;
  currentSection: string;
  onSectionChange: (newSection: string) => void;
  interval: StatsIntervalIds;
  onIntervalChange: (newInterval: StatsIntervalIds) => void;
  onFilterInputChange: (q: string) => void;
  isLoading: boolean;
  initialFilterValue: string;
};

const StatsFilters = ({
  sections,
  currentSection,
  onSectionChange,
  interval,
  onIntervalChange,
  onFilterInputChange,
  isLoading,
  initialFilterValue,
}: Props) => {
  const [ filterQuery, setFilterQuery ] = React.useState(initialFilterValue || '');

  const options = React.useMemo(() => {
    return [
      { value: 'all', label: 'All stats' },
      ...(sections || []).map((section) => ({ value: section.id, label: section.title })),
    ];
  }, [ sections ]);

  const handleFilterChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterQuery(value);
    onFilterInputChange(value);
  }, [ onFilterInputChange ]);

  return (
    <div className={ styles.grid }>
      <div className={ styles.section }>
        <select
          className={ styles.select }
          value={ currentSection }
          onChange={ (e) => onSectionChange(e.target.value) }
          disabled={ isLoading }
        >
          { options.map((option) => (
            <option key={ option.value } value={ option.value }>
              { option.label }
            </option>
          )) }
        </select>
      </div>

      <div className={ styles.interval }>
        <ChartIntervalSelect interval={ interval } onIntervalChange={ onIntervalChange } isLoading={ isLoading }/>
      </div>

      <div className={ styles.input }>
        <div className={ styles.searchWrapper }>
          <MdSearch className={ styles.searchIcon } size={ 18 }/>
          <input
            type="text"
            className={ styles.searchInput }
            placeholder="Find chart, metric..."
            value={ filterQuery }
            onChange={ handleFilterChange }
            disabled={ isLoading }
          />
        </div>
      </div>
    </div>
  );
};

export default StatsFilters;
